/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {
  FunctionComponent,
  ReactNode,
  useEffect,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { Alert } from "react-bootstrap";
import "../Login/Login.css";
import "./ForgotPassword.css";
import { APICall } from "../../Helpers/Network/APICalls";
import {
  CheckForgotPasswordKey,
  UpdatePassword,
} from "../../Helpers/APIEndPoints/EndPoints";
import { ApiStatus } from "../../Helpers/Enums/ApiStatus";
import notify, { ToastType } from "../../Helpers/ToastNotification";
import { AlertType } from "../../Helpers/Enums/AlertType";
import { Backdrop, CircularProgress } from "@mui/material";
import ComponentLoader from "../../Helpers/Loader/ComponentLoader";
import { CheckForgotPasswordKeyResponseModel } from "../../Models/CheckForgotPasswordKeyResponseModel";
import { GenericResponseModel } from "../../Models/GenericResponseModel";
import { AutoCompleteValue } from "../../Components/InputForm/InputForm";

const ForgotPassword = () => {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const initialAlert = {
    message: "",
    type: AlertType.error,
  };
  const [alertObj, setAlertObj] = useState<{
    message: ReactNode | string;
    type: AlertType;
  }>(initialAlert);
  const [showFields, setShowFields] = useState<boolean>(false);
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [isSubmitClicked, setIsSubmitClicked] = useState<boolean>(false);
  const [loginId, setLoginId] = useState<string>("");
  const [validationList, setValidationList] = useState<Array<any>>([
    { isSuccess: false, message: "1 Capital letter" },
    { isSuccess: false, message: "1 Numeric character" },
    { isSuccess: false, message: "1 Special character" },
    { isSuccess: false, message: "Minimum 8 character" },
    { isSuccess: false, message: "Maximum 16 character" },
  ]);

  useEffect(() => {
    const userKey = searchParams.get("user");
    checkForgetPassKey(userKey);
  }, [searchParams]);

  const checkForgetPassKey = async (ForgotPasswordKey) => {
    if (ForgotPasswordKey) {
      setShowLoader(true);
      try {
        let resp = await APICall(CheckForgotPasswordKey, {
          ForgotPasswordKey: ForgotPasswordKey,
        });

        if (resp.Status === ApiStatus.OK) {
          if (resp.Data) {
            let loginResp = resp.Data as CheckForgotPasswordKeyResponseModel;
            if (loginResp.Status) {
              setShowFields(true);
              setLoginId(loginResp.LoginId);
            } else {
              setAlertObj({
                ...alertObj,
                message: loginResp.Message,
                type: AlertType.error,
              });
            }
          }
        } else {
          setAlertObj({
            ...alertObj,
            message: resp.Message,
            type: AlertType.error,
          });
        }
      } catch (err) {
      } finally {
        setShowLoader(false);
      }
    } else {
      setAlertObj({
        ...alertObj,
        message: "Invalid Request",
        type: AlertType.error,
      });
    }
  };

  const updatePassword = async () => {
    setIsSubmitClicked(true);
    if (!password || !confirmPassword || password !== confirmPassword) {
      return;
    }
    setShowLoader(true);
    try {
      let resp = await APICall(UpdatePassword, {
        LoginId: loginId,
        Password: password,
        ConfirmPassword: confirmPassword,
      });

      if (resp.Status === ApiStatus.OK) {
        if (resp.Data) {
          let loginResp = resp.Data as GenericResponseModel;
          if (loginResp.Status) {
            setShowFields(false);
            setAlertObj({
              ...alertObj,
              message: (
                <span>
                  Password reset successful, <a href="/login">Click here</a> to
                  Login
                </span>
              ),
              type: AlertType.success,
            });
          } else {
            setAlertObj({
              ...alertObj,
              message: loginResp.Message,
              type: AlertType.error,
            });
          }
        }
      } else {
        setAlertObj({
          ...alertObj,
          message: resp.Message,
          type: AlertType.error,
        });
      }
    } catch (err) {
    } finally {
      setShowLoader(false);
    }
  };

  enum TextInputTarget {
    Password,
    ConfirmPassword,
  }

  const checkValidation = (value: string, target: TextInputTarget) => {
    switch (target) {
      case TextInputTarget.Password:
        const newValidationList = [];
        newValidationList.push({
          isSuccess: /.*[A-Z].*/.test(value),
          message: "1 Capital letter",
        });
        newValidationList.push({
          isSuccess: /.*\d.*/.test(value),
          message: "1 Numeric character",
        });
        newValidationList.push({
          isSuccess: /.*[!@#$%^&*()\-_=+{}[\]:;"'<>,.?].*/.test(value),
          message: "1 Special character",
        });
        newValidationList.push({
          isSuccess: value.length > 8,
          message: "Minimum 8 character",
        });
        newValidationList.push({
          isSuccess: value.length < 16,
          message: "Maximum 16 character",
        });
        
        setValidationList(newValidationList);
        break;
      case TextInputTarget.ConfirmPassword:
        break;
    }
  };

  const onTextChanged = (event, target: TextInputTarget) => {
    const value = event.target.value;
    switch (target) {
      case TextInputTarget.Password:
        setPassword(value);
        checkValidation(value, target);
        break;
      case TextInputTarget.ConfirmPassword:
        setConfirmPassword(value);
        break;
    }
  };

  enum ButtonClickTarget {
    ResetPassword,
  }

  const onButtonClick = (target: ButtonClickTarget) => {
    switch (target) {
      case ButtonClickTarget.ResetPassword:
        updatePassword();
        break;
    }
  };

  const ValidationSpan = (props) => {
    const { isSuccess, message } = props;
    return (
      <span className={"px-3 " + (isSuccess ? "text-success" : "text-danger")}>
        <i
          className={`fa-solid ${isSuccess ? "fa-check" : "fa-xmark"} px-2`}
        ></i>
        {message}
      </span>
    );
  };

  return (
    <div className="login_wrapper">
      <div className="container-fluid">
        <div className="row">
          <div className="col-lg-6 p-0">
            <div className="login_left_bg">
              <div className="left_bg_inner text-center">
                <img src="images/mahindra_logo.jpg" alt="Mahindra Logo" />
              </div>
            </div>
          </div>
          <div className="col-lg-6 p-0">
            <div className="login_right_bg">
              <div className="right_bg_inner">
                <div className="text-center">
                  <img src="images/Logo.png" alt="Pride Logo" />
                  <div>
                    <b>Welcome to Mahindra Credit Society</b>
                  </div>
                </div>
                <ComponentLoader show={showLoader}>
                  {/* <!-- Nav tabs --> */}
                  <ul className="nav nav-tabs">
                    <li className="nav-item nav-tab-full">
                      <a className="nav-link active" data-toggle="tab">
                        Reset Password
                      </a>
                    </li>
                  </ul>

                  {/* <!-- Tab panes --> */}
                  {showFields && (
                    <div className="tab-content">
                      <div className="tab-pane active" id="tab1">
                        <div className="form_grid">
                          <div className="form-group">
                            {/* <!-- <label>Email ID</label> --> */}
                            <div className="input_grid">
                              <input
                                autoComplete={AutoCompleteValue}
                                type="password"
                                name=""
                                placeholder="Enter your new password"
                                className="form-control"
                                onChange={(event: React.ChangeEvent) =>
                                  onTextChanged(event, TextInputTarget.Password)
                                }
                              />
                              <i className="fa fa-lock"></i>
                            </div>
                            {isSubmitClicked && !password && (
                              <div className="pt-3">
                                <ValidationSpan
                                  isSuccess={false}
                                  message="This field is required"
                                />
                              </div>
                            )}
                          </div>
                          {password && (
                            <div className="pb-3">
                              <ul>
                                <span>Password must contain : </span>
                                {validationList.map((v) => {
                                  return <ValidationSpan {...v} />;
                                })}
                              </ul>
                            </div>
                          )}
                          <div className="form-group">
                            {/* <!-- <label>Password</label> --> */}
                            <div className="input_grid">
                              <input
                                autoComplete={AutoCompleteValue}
                                type="password"
                                name=""
                                placeholder="Re-enter your new password"
                                className="form-control"
                                onChange={(event: React.ChangeEvent) =>
                                  onTextChanged(
                                    event,
                                    TextInputTarget.ConfirmPassword
                                  )
                                }
                              />
                              <i className="fa fa-lock"></i>
                            </div>
                            {isSubmitClicked && !confirmPassword && (
                              <div className="pt-3">
                                <ValidationSpan
                                  isSuccess={false}
                                  message="This field is required"
                                />
                              </div>
                            )}
                            {password &&
                              confirmPassword &&
                              password !== confirmPassword && (
                                <div className="pt-3">
                                  <ValidationSpan
                                    isSuccess={password === confirmPassword}
                                    message="Password doesn't match"
                                  />
                                </div>
                              )}
                          </div>
                          <div className="text-center">
                            <button
                              className="btn btn-signUp"
                              onClick={() =>
                                onButtonClick(ButtonClickTarget.ResetPassword)
                              }
                            >
                              Confirm
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {alertObj.message && (
                    <div className="p-3">
                      <Alert
                        variant={
                          alertObj.type === AlertType.error
                            ? "danger"
                            : "success"
                        }
                        onClose={() =>
                          setAlertObj({ ...alertObj, ...initialAlert })
                        }
                        dismissible={alertObj.type !== AlertType.error}
                      >
                        <p>{alertObj.message}</p>
                      </Alert>
                    </div>
                  )}
                </ComponentLoader>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
